import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import A from '@adsk/alloy-react-a';
import Banner, { BANNER_VARIANTS } from '@adsk/alloy-react-banner';
import { useLocale } from '@adsk/alloy-react-helpers';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import Tooltip from '@adsk/alloy-react-tooltip';
import LoadSubscriptionError from '../LoadSubscriptionError';
import OfferingToggle from '../OfferingToggle';
import OfferingDropdown from '../OfferingDropdown';
import OfferingTrialMessage from '../OfferingTrialMessage';
import {
  emptyMessageStyle,
  iconStyle,
  progressRingStyle,
  titleStyle,
  titleTextStyle,
  warningBannerStyle,
  noSubscriptionMessageStyle,
} from './styles';
import { licenseDropdownPropType } from '../../propTypes';
import { LICENSE_INPUT_TYPES } from '../../constants';
import { getContactLink } from '../../utils';

const MemberSubscription = ({
  licensingTrialMessages,
  loadingSubscriptions,
  offerings,
  offeringsDataError,
  onLicenseChange,
  onTryAgainClick,
  overSheetLimitData,
  savingDropdown,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const userSheetCount = overSheetLimitData?.userSheetCount;
  const isOverSheetBanner = overSheetLimitData?.isOverSheetBanner;
  const hideOverSheetBanner = overSheetLimitData?.hideOverSheetBanner;
  const sheetCountError = overSheetLimitData?.sheetCountError;

  const renderCategoryInputs = useCallback(() => {
    if (!offerings) return null;

    const inputs = [];

    const sheetCountValidation = (showSheets) => {
      if (!showSheets) {
        return null;
      }

      return sheetCountError ? -1 : overSheetLimitData?.userSheetCount;
    };

    Object.entries(offerings).forEach(([category, offeringsCategoryData]) => {
      if (offeringsCategoryData.showOffering) {
        switch (offeringsCategoryData.inputType) {
          case LICENSE_INPUT_TYPES.TOGGLE: {
            const option = offeringsCategoryData.options[0];

            inputs.push(
              <OfferingToggle
                checked={!!offeringsCategoryData.selectedOffering}
                customizedDisabledTextKey={
                  offeringsCategoryData.customizedDisabledTextKey
                }
                customizedLabelDescriptionKey={
                  offeringsCategoryData.customizedLabelDescriptionKey
                }
                disabled={offeringsCategoryData.disabledLicense}
                key={category}
                label={offeringsCategoryData.title}
                licensesAvailable={option.quantity.available}
                licenseType={offeringsCategoryData.options[0].type}
                onChange={(checked) =>
                  onLicenseChange(checked ? option : null, category)
                }
              />
            );
            break;
          }
          case LICENSE_INPUT_TYPES.DROPDOWN:
            inputs.push(
              <OfferingDropdown
                key={category}
                category={category}
                external={offeringsCategoryData.disabledLicense}
                label={offeringsCategoryData.title}
                onChange={(value) => onLicenseChange(value, category)}
                options={offeringsCategoryData.options}
                saving={
                  savingDropdown.saving && category === savingDropdown.category
                }
                userSheetCount={sheetCountValidation(
                  offeringsCategoryData.showSheets
                )}
                value={offeringsCategoryData.selectedOffering}
              />
            );
            break;
          default:
            break;
        }

        if (
          licensingTrialMessages?.subscriptionMessages &&
          licensingTrialMessages?.subscriptionMessages[category]
        ) {
          inputs.push(
            <OfferingTrialMessage
              key={`${category}-trial-message`}
              member={licensingTrialMessages?.member}
              subscriptionMessage={
                licensingTrialMessages.subscriptionMessages[category]
              }
            />
          );
        }
      }
    });

    if (inputs.length === 0) {
      const expectedURL = getContactLink();

      return (
        <>
          <div css={noSubscriptionMessageStyle}>
            {T.translate('NO_ACTIVE_SUBSCRIPTIONS_MESSAGE')}
          </div>

          <div css={emptyMessageStyle}>
            {T.translate('NO_SUBSCRIPTIONS_MESSAGE', {
              activatePurchasedLink: (
                <A href={expectedURL} target="_blank">
                  {T.translate('ACTIVATE_PURCHASED')}
                </A>
              ),
            })}
          </div>
        </>
      );
    }

    return inputs;
  }, [
    licensingTrialMessages,
    offerings,
    onLicenseChange,
    T,
    savingDropdown,
    userSheetCount,
  ]);

  const renderSubscriptionContent = () => {
    if (loadingSubscriptions) {
      return (
        <div
          css={progressRingStyle}
          data-testid="AccountMemberProfilePanel_MemberSubscription_progressRing"
        >
          <ProgressRing size="medium" />
        </div>
      );
    }

    if (offeringsDataError) {
      return (
        <LoadSubscriptionError
          data-testid="AccountMemberProfilePanel_MemberSubscription_subscriptionError"
          onTryAgainClick={onTryAgainClick}
        />
      );
    }

    return (
      <div>
        {renderCategoryInputs()}
        {isOverSheetBanner && (
          <Banner
            message={T.translate('MEMBER_SUBSCRIPTION_OVER_SHEET_LIMIT_TEXT')}
            onClose={hideOverSheetBanner}
            variant={BANNER_VARIANTS.WARNING}
            inline
            style={warningBannerStyle}
          />
        )}
      </div>
    );
  };

  return (
    <div data-testid="AccountMemberProfilePanel_MemberSubscription">
      <div css={titleStyle}>
        <h3 css={titleTextStyle}>{T.translate('MEMBER_SUBSCRIPTION_TITLE')}</h3>
        <Tooltip
          content={T.translate('MEMBER_SUBSCRIPTION_TITLE_TOOLTIP_TEXT')}
          placement={Tooltip.PLACEMENTS.BOTTOM_END}
        >
          <AlertInformationIcon style={iconStyle} />
        </Tooltip>
      </div>
      {renderSubscriptionContent()}
    </div>
  );
};

MemberSubscription.propTypes = {
  loadingSubscriptions: PropTypes.bool.isRequired,
  offerings: PropTypes.objectOf(licenseDropdownPropType),
  offeringsDataError: PropTypes.object,
  onLicenseChange: PropTypes.func.isRequired,
  onTryAgainClick: PropTypes.func.isRequired,
  overSheetLimitData: PropTypes.object,
  savingDropdown: PropTypes.object.isRequired,
  licensingTrialMessages: PropTypes.shape({
    member: PropTypes.string,
    subscriptionMessages: PropTypes.objectOf(
      PropTypes.shape({
        daysRemaining: PropTypes.number,
        textKey: PropTypes.string,
      })
    ),
  }),
};

MemberSubscription.defaultProps = {
  offerings: null,
  offeringsDataError: null,
  overSheetLimitData: null,
  licensingTrialMessages: null,
};

export default MemberSubscription;
